import {useCallback} from 'react'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import {isCopilot} from '../../helpers/assignee-helper'
import {useBulkUpdateItems} from '../../hooks/use-bulk-update-items'
import type {MemexItemModel} from '../../models/memex-item-model'
import {shouldOpenCopilotModal, useCopilotAssignmentModal} from './copilot-assignment-modal-provider'
import type {CopilotAssigneeUpdateHandler} from './hooks/use-copy-paste/update-items-or-error'

/**
 * Helper hook to handle Copilot assignee bulk updates with modal logic.
 * Can be used for both drag-to-fill and paste operations.
 *
 * @param description - Description for the history stack (e.g., 'Fill cells', 'Paste')
 * @returns Function that handles copilot assignee updates with modal logic
 */
export const useCopilotAssigneeUpdate = (description: string): CopilotAssigneeUpdateHandler => {
  const {bulkUpdateSingleColumnValue: bulkUpdateItems} = useBulkUpdateItems()
  const {openCopilotModalForIssues} = useCopilotAssignmentModal()

  return useCallback<CopilotAssigneeUpdateHandler>(
    async (itemsToUpdate, update, sourceRepoId) => {
      if (update.dataType !== MemexColumnDataType.Assignees || !shouldOpenCopilotModal(itemsToUpdate, update)) {
        return false
      }

      const sourceAssignees = update.value
      const copilotAssignees = sourceAssignees.filter(assignee => isCopilot(assignee.login))
      const nonCopilotAssignees = sourceAssignees.filter(assignee => !isCopilot(assignee.login))

      if (copilotAssignees.length > 0) {
        // First, apply non-Copilot assignees immediately if any
        if (nonCopilotAssignees.length > 0) {
          const nonCopilotUpdate = {...update, value: nonCopilotAssignees}
          await bulkUpdateItems(itemsToUpdate, nonCopilotUpdate, description, sourceRepoId)
        }

        // Create the update function that will be called by the modal
        const updateOnSuccess = async () => {
          // Apply Copilot assignees to all items after modal completion
          const copilotUpdate = {...update, value: [...nonCopilotAssignees, ...copilotAssignees]}
          await bulkUpdateItems(itemsToUpdate, copilotUpdate, description, sourceRepoId)
        }

        // Use the shared utility to handle the modal logic
        // Only apply repository filter if sourceRepoId is provided
        const repositoryFilter = sourceRepoId
          ? (item: MemexItemModel) => item.contentRepositoryId === sourceRepoId
          : undefined
        const isModalOpened = openCopilotModalForIssues(itemsToUpdate, updateOnSuccess, repositoryFilter)
        return isModalOpened
      }

      return false
    },
    [bulkUpdateItems, openCopilotModalForIssues, description],
  )
}
